import { useState, useEffect } from 'react';
import { fieldStyle, labelStyle, inputStyle } from '../../constants/general';
import Select from 'react-select';

export const BasicInfo = ({ formik, projects, channels }) => {
  const [asanaProjects, setAsanaProjects] = useState([]);
  const [asanaAssignees, setAsanaAssignees] = useState([]);
  const [slackChannels, setSlackChannels] = useState([]);

  useEffect(() => {
    if (projects && projects?.length) {
      setAsanaProjects(projects.map(p => ({ value: p.gid, label: p.name })));
    }
  }, [projects]);

  useEffect(() => {
    if (channels && channels?.length) {
      setSlackChannels(channels.map(p => ({ value: p.id, label: p.name })));
    }
  }, [channels]);

  useEffect(() => {
    if (projects && projects?.length && formik.values.asanaProject) {
      const asanaProjectId = formik.values.asanaProject.value;
      const projectMembers = projects.find(p => p.gid === asanaProjectId)?.members;

      setAsanaAssignees(projectMembers?.map(m => ({ value: m.gid, label: m.name })));
    }
  }, [projects, formik.values.asanaProject]);

  return (
    <>
      <div className={fieldStyle}>
        <label htmlFor="siteUrl" className={labelStyle}>
          Site URL
        </label>

        <input
          id="siteUrl"
          type="text"
          className={inputStyle}
          value={formik.values.siteUrl}
          onChange={formik.handleChange}
        />
        {formik.touched.siteUrl && formik.errors.siteUrl ? (
          <div className="text-error text-sm mt-1">{formik.errors.siteUrl}</div>
        ) : null}

        <div className="flex flex-col mt-4 mb-2">
          <label className="flex items-center gap-2 cursor-pointer w-fit">
            <input
              id="displaySiteUrl"
              type="checkbox"
              className="w-4 h-4 cursor-pointer"
              checked={formik.values.displaySiteUrl}
              onChange={formik.handleChange}
            />
            <span className="font-semibold">Display site URL on feedback form</span>
          </label>
        </div>
      </div>

      <div className={fieldStyle}>
        <label htmlFor="siteName" className={labelStyle}>
          Site Name
        </label>

        <input
          id="siteName"
          type="text"
          className={inputStyle}
          value={formik.values.siteName}
          onChange={formik.handleChange}
        />
        {formik.touched.siteName && formik.errors.siteName ? (
          <div className="text-error text-sm mt-1">{formik.errors.siteName}</div>
        ) : null}
      </div>

      <div className={fieldStyle}>
        <label htmlFor="asanaProject" className={labelStyle}>
          Asana Project
        </label>

        <Select
          options={asanaProjects}
          value={formik.values.asanaProject}
          onChange={value => {
            formik.setFieldValue('asanaProject', value);
            formik.setFieldValue('asanaAssignee', {});
          }}
          styles={{
            input: base => ({
              ...base,
              marginLeft: 10,
              padding: 0,
              '& input': {
                padding: '0 !important',
                margin: '0 !important',
                paddingLeft: '0 !important',
              },
              'input:focus': {
                boxShadow: 'none',
              },
            }),
            valueContainer: base => ({
              ...base,
              padding: '2px 8px',
              paddingLeft: '8px',
            }),
            placeholder: base => ({
              ...base,
              marginLeft: 10,
            }),
            singleValue: base => ({
              ...base,
              marginLeft: 10,
            }),
          }}
        />
        {formik.touched.asanaProject && formik.errors.asanaProject ? (
          <div className="text-error text-sm mt-1">{formik.errors.asanaProject}</div>
        ) : null}
      </div>

      <div className={fieldStyle}>
        <label htmlFor="asanaAssignee" className={labelStyle}>
          Asana Assignee
        </label>
        <p className="mt-2 mb-4 p-3 italic text-sm border border-blue-200 bg-blue-50 rounded">
          <span className="font-semibold not-italic">Optional field: </span>Choose a team member to
          automatically assign new feedback submissions, or leave it blank to create unassigned
          tasks.
        </p>

        <Select
          options={asanaAssignees}
          isDisabled={!Object.keys(formik.values.asanaProject).length}
          value={formik.values.asanaAssignee}
          onChange={value => {
            formik.setFieldValue('asanaAssignee', value);
          }}
          isClearable
          styles={{
            input: base => ({
              ...base,
              marginLeft: 10,
              padding: 0,
              '& input': {
                padding: '0 !important',
                margin: '0 !important',
                paddingLeft: '0 !important',
              },
              'input:focus': {
                boxShadow: 'none',
              },
            }),
            valueContainer: base => ({
              ...base,
              padding: '2px 8px',
              paddingLeft: '8px',
            }),
            placeholder: base => ({
              ...base,
              marginLeft: 10,
            }),
            singleValue: base => ({
              ...base,
              marginLeft: 10,
            }),
          }}
        />
      </div>

      <div className={fieldStyle}>
        <label htmlFor="slackChannel" className={labelStyle}>
          Slack Channel
        </label>

        <Select
          options={slackChannels.map(channel => ({
            ...channel,
            label: `# ${channel.label}`,
          }))}
          value={formik.values.slackChannel}
          onChange={value => {
            formik.setFieldValue('slackChannel', value);
          }}
          styles={{
            input: base => ({
              ...base,
              marginLeft: 10,
              padding: 0,
              '& input': {
                padding: '0 !important',
                margin: '0 !important',
                paddingLeft: '0 !important',
              },
              'input:focus': {
                boxShadow: 'none',
              },
            }),
            valueContainer: base => ({
              ...base,
              padding: '2px 8px',
              paddingLeft: '8px',
            }),
            placeholder: base => ({
              ...base,
              marginLeft: 10,
            }),
            singleValue: base => ({
              ...base,
              marginLeft: 10,
            }),
          }}
        />
      </div>

      <div className={fieldStyle}>
        <label htmlFor="subscribers" className={labelStyle}>
          Subscriber Emails
        </label>

        <div className="space-y-2">
          {formik.values.subscribers?.map((email, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="email"
                className={inputStyle}
                placeholder="email@example.com"
                value={email}
                onChange={e => {
                  const newSubscribers = [...formik.values.subscribers];
                  newSubscribers[index] = e.target.value;
                  formik.setFieldValue('subscribers', newSubscribers);
                }}
              />
              <button
                type="button"
                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={() => {
                  const newSubscribers = formik.values.subscribers.filter((_, i) => i !== index);
                  formik.setFieldValue('subscribers', newSubscribers);
                }}
              >
                Remove
              </button>
            </div>
          ))}
          {formik.touched.subscribers && formik.errors.subscribers ? (
            <div className="text-error text-sm mt-1">{formik.errors.subscribers}</div>
          ) : null}

          <button
            type="button"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => {
              formik.setFieldValue('subscribers', [...(formik.values.subscribers || []), '']);
            }}
          >
            + Add Email
          </button>
        </div>
      </div>
    </>
  );
};
